/**
 * 포트폴리오 스텝 추적 훅
 * 
 * 포트폴리오 생성 프로세스의 각 단계를 추적
 * - portfolio_step_view: 스텝 진입
 * - portfolio_step_submit: 스텝 완료 (성공/실패)
 * 
 * ⚠️ intent_id는 내부적으로 portfolio_flow 의도의 intent_id를 세션 스토리지에서 자동으로 참조합니다.
 * 
 * @example
 * ```tsx
 * function PortfolioStepPage() {
 *   const portfolioStep = usePortfolioStep();
 *   const [currentStep, setCurrentStep] = useState<PortfolioStepKey>('search_select');
 *   
 *   useEffect(() => {
 *     // 스텝 진입 (intent_id는 portfolio_flow에서 자동으로 가져옴)
 *     portfolioStep.view({
 *       step_key: currentStep,
 *       step_index: 1,
 *     });
 *   }, [currentStep]);
 *   
 *   const handleSubmitStep = async (data: any) => {
 *     try {
 *       await processStep(currentStep, data);
 *       
 *       // 스텝 완료 (성공)
 *       portfolioStep.submit({
 *         step_key: currentStep,
 *         step_index: 1,
 *         outcome: 'ok',
 *         tickers: data.tickers,  // search_select 시
 *       });
 *       
 *       setCurrentStep(getNextStep(currentStep));
 *     } catch (error) {
 *       // 스텝 완료 (실패)
 *       portfolioStep.submit({
 *         step_key: currentStep,
 *         step_index: 1,
 *         outcome: 'fail',
 *         fail_reason: 'validation_error',
 *       });
 *     }
 *   };
 * }
 * ```
 */

import { useCallback } from 'react';
import { ulid } from 'ulid';
import { track } from '../../../../core/client';
import { getIntentId } from '../../../../core/ids';
import type { PortfolioStepKey } from '../../../../core/schema';

export interface PortfolioStepViewParams {
    step_key: PortfolioStepKey;
    step_index: number;
    portfolio_id?: string | number;
}

export interface PortfolioStepSubmitParams {
    step_key: PortfolioStepKey;
    step_index: number;
    sub_step_index?: number;
    outcome: 'ok' | 'fail';
    fail_reason?: string;
    choice?: 'input' | 'skip' | 'proceed';
    submit_mode?: 'client_only' | 'server_request';
    tickers?: string[];
    weights?: number[];
    answer_key?: string;
}

export interface PortfolioStepActions {
    view: (params: PortfolioStepViewParams) => void;
    submit: (params: PortfolioStepSubmitParams) => void;
}

/**
 * 포트폴리오 스텝 추적 훅
 */
export function usePortfolioStep(): PortfolioStepActions {
    /**
     * portfolio_flow 의도의 intent_id를 세션 스토리지에서 가져오기
     * 없으면 fallback으로 새로 생성
     */
    const getPortfolioFlowIntentId = useCallback((): string | null => {
        const portfolioFlowIntent = getIntentId('portfolio_flow');
        if (portfolioFlowIntent?.intentId) {
            return portfolioFlowIntent.intentId;
        }
        // fallback: portfolio_flow가 없으면 새로 생성 (정상적인 flow에서는 발생하지 않아야 함)
        console.warn('[PortfolioStep] portfolio_flow intent_id not found in session storage, generating new one');
        return null; //ulid(); // 회원 - 설문 재시도 시 portfolio_flow 의도가 없어서 null 반환
    }, []);

    /**
     * 스텝 진입 추적
     */
    const view = useCallback((params: PortfolioStepViewParams) => {
        const intentId = getPortfolioFlowIntentId();

        track('portfolio_step_view', {
            intent_id: intentId,
            step_key: params.step_key,
            step_index: params.step_index,
            portfolio_id: params.portfolio_id,
        });

    }, [getPortfolioFlowIntentId]);

    /**
     * 스텝 완료 추적
     */
    const submit = useCallback((params: PortfolioStepSubmitParams) => {
        const intentId = getPortfolioFlowIntentId();

        track('portfolio_step_submit', {
            intent_id: intentId,
            step_key: params.step_key,
            step_index: params.step_index,
            sub_step_index: params.sub_step_index,
            outcome: params.outcome,
            fail_reason: params.fail_reason,
            choice: params.choice,
            submit_mode: params.submit_mode,
            tickers: params.tickers,
            weights: params.weights,
            answer_key: params.answer_key,
        });

    }, [getPortfolioFlowIntentId]);

    return {
        view,
        submit,
    };
}

export type { PortfolioStepKey };

